import { useMemo, useState } from 'react';
import { apiPost } from '../../lib/apiClient.js';
import {
  cleanLabelText,
  labelChipStyle,
  labelKey,
  LABEL_COLORS,
  suggestLabelColor,
} from '../../lib/crmLabels.js';
import type { CrmLabel } from '../../lib/types/customers.js';
import { useApiGet } from '../../lib/useApiGet.js';
import './crmLabels.css';

interface LabelListResponse {
  readonly items: readonly CrmLabel[];
}

/**
 * Label picker for customers and leads: type to filter the tenant's labels and pick one, or
 * create a new coloured label right there. A record can carry several; each shows as a removable
 * chip. `canCreate` comes from the server (the user's own rights) — without it the field only
 * picks. `disabled` shows the current labels read-only.
 */
export function LabelField({
  base,
  value,
  onChange,
  canCreate,
  disabled,
  initialLabels,
}: {
  readonly base: string;
  readonly value: readonly string[];
  readonly onChange: (labelIds: string[]) => void;
  readonly canCreate: boolean;
  readonly disabled?: boolean;
  /** Labels already on the record (may include archived ones the catalogue no longer offers). */
  readonly initialLabels?: readonly CrmLabel[];
}) {
  const [refresh, setRefresh] = useState(0);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const list = useApiGet<LabelListResponse>(`${base}/api/v1/crm/labels?status=active`, [
    base,
    refresh,
  ]);
  const catalogue = useMemo(() => (list.status === 'ready' ? list.data.items : []), [list]);
  const byId = useMemo(() => {
    const map = new Map<string, CrmLabel>();
    for (const label of initialLabels ?? []) map.set(label.labelId, label);
    for (const label of catalogue) map.set(label.labelId, label);
    return map;
  }, [catalogue, initialLabels]);

  const query = labelKey(text);
  const selected = new Set(value);
  const suggestions = catalogue.filter(
    (label) =>
      !selected.has(label.labelId) && (query === '' || labelKey(label.name).includes(query)),
  );
  const exact = catalogue.find((label) => labelKey(label.name) === query);
  const creatable = canCreate && query !== '' && !exact;
  const chosenColor = color ?? suggestLabelColor(catalogue.map((label) => label.color));

  const add = (id: string) => {
    if (!selected.has(id)) onChange([...value, id]);
    setText('');
    setOpen(false);
  };

  async function createLabel() {
    const name = cleanLabelText(text);
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      // The server returns the existing label when someone already made this name, so a race or
      // a retry never produces a duplicate.
      const created = await apiPost<CrmLabel>(`${base}/api/v1/crm/labels`, {
        name,
        color: chosenColor,
      });
      setRefresh((k) => k + 1);
      add(created.labelId);
      setColor(null);
    } catch {
      setError('ساخت برچسب ناموفق بود. دسترسی یا اتصال را بررسی کنید.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="crm-label-field">
      {value.length > 0 ? (
        <div className="crm-labels">
          {value.map((id) => {
            const label = byId.get(id);
            return (
              <span
                key={id}
                className="crm-label"
                style={labelChipStyle(label?.color ?? null)}
                data-inactive={label?.status === 'inactive' ? 'true' : undefined}
              >
                {label?.name ?? '…'}
                {disabled ? null : (
                  <button
                    type="button"
                    className="crm-label__remove"
                    aria-label={`حذف برچسب ${label?.name ?? ''}`}
                    onClick={() => onChange(value.filter((v) => v !== id))}
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}
        </div>
      ) : disabled ? (
        <span className="c360-note">برچسبی ثبت نشده است.</span>
      ) : null}
      {disabled ? null : (
        <div style={{ position: 'relative' }}>
          <input
            className="sk-input"
            value={text}
            placeholder={canCreate ? 'جستجو یا ساخت برچسب جدید…' : 'جستجوی برچسب…'}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={(event) => {
              setText(event.target.value);
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault(); // Enter must not submit the surrounding form
              if (exact && !selected.has(exact.labelId)) add(exact.labelId);
              else if (creatable && !busy) void createLabel();
            }}
          />
          {open && (suggestions.length > 0 || creatable) ? (
            <div role="listbox" className="sk-card crm-label-field__menu">
              {suggestions.map((label) => (
                <button
                  key={label.labelId}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => add(label.labelId)}
                >
                  <span className="crm-label" style={labelChipStyle(label.color)}>
                    {label.name}
                  </span>
                </button>
              ))}
              {creatable ? (
                <>
                  <div className="crm-label-field__colors" onMouseDown={(e) => e.preventDefault()}>
                    <span>رنگ:</span>
                    {LABEL_COLORS.map((c) => (
                      <button
                        key={c.hex}
                        type="button"
                        className="crm-label-field__swatch"
                        style={labelChipStyle(c.hex)}
                        title={c.name}
                        aria-label={c.name}
                        aria-pressed={chosenColor === c.hex}
                        onClick={() => setColor(c.hex)}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="sk-btn sk-btn--ghost sk-btn--sm"
                    disabled={busy}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => void createLabel()}
                  >
                    {busy ? 'در حال ساخت…' : `+ ساخت برچسب «${cleanLabelText(text)}»`}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {error ? <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>{error}</span> : null}
    </div>
  );
}
